export function PageHeader({ title, subtitle, icon: Icon, action }) {
  return (
    <div className="mb-6 border-b border-amber-900/30 pb-4 fade-up flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div className="flex items-start gap-3 min-w-0">
        {Icon && <Icon className="text-amber-500" size={26} />}
        <div className="min-w-0">
          <h1 className="font-cinzel text-2xl sm:text-3xl text-amber-300 uppercase tracking-wide">{title}</h1>
          {subtitle && <div className="text-zinc-500 text-sm mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {action ? <div className="w-full lg:w-auto lg:shrink-0">{action}</div> : null}
    </div>
  );
}

export function Loader({ label = "Loading…" }) {
  return <div className="text-amber-500/70 font-cinzel py-16 text-center animate-pulse">{label}</div>;
}

export function Empty({ label, children }) {
  return (
    <div className="border border-dashed border-amber-900/40 bg-zinc-950/50 p-10 text-center">
      <p className="text-zinc-500 font-manrope">{label}</p>
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

export function btn(variant = "primary") {
  const base = "px-5 py-2.5 uppercase tracking-[0.18em] font-cinzel text-sm transition-all border disabled:opacity-40 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-amber-900/20 text-amber-300 border-amber-700/50 hover:bg-amber-900/40 hover:border-amber-500",
    danger: "bg-red-900/20 text-red-400 border-red-900/50 hover:bg-red-900/40 hover:border-red-500",
    ghost: "bg-transparent text-zinc-400 border-zinc-800 hover:text-amber-300 hover:border-amber-700/50",
  };
  return `${base} ${variants[variant]}`;
}

import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Reusable pager: Prev / Next buttons + a "jump to page" number input.
// testid prefixes the control ids (e.g. testid="rankings" -> rankings-prev/-next/-input).
export function Pagination({ page, totalPages, onChange, testid = "pagination", prevLabel = "Prev", nextLabel = "Next", className = "" }) {
  const [val, setVal] = useState(String(page));
  useEffect(() => { setVal(String(page)); }, [page]);
  if (totalPages <= 1) return null;

  const go = () => {
    let n = parseInt(val, 10);
    if (!n || n < 1) n = 1;
    if (n > totalPages) n = totalPages;
    setVal(String(n));
    if (n !== page) onChange(n);
  };

  const navBtn = "flex items-center gap-1 px-3 py-2 border border-amber-900/40 text-zinc-300 hover:border-amber-600 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-sm text-sm";

  return (
    <div data-testid={testid} className={`flex items-center justify-center gap-2 ${className}`}>
      <button data-testid={`${testid}-prev`} disabled={page <= 1} onClick={() => onChange(page - 1)} className={navBtn}>
        <ChevronLeft size={15} /> {prevLabel}
      </button>
      <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-500">
        Page
        <input data-testid={`${testid}-input`} type="number" min={1} max={totalPages} value={val}
          onChange={(e) => setVal(e.target.value)} onBlur={go}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); e.target.blur(); } }}
          aria-label="Go to page"
          className="w-14 bg-zinc-950 border border-amber-900/40 text-amber-300 px-2 py-2 outline-none focus:border-amber-600 tabular-nums text-center rounded-sm" />
        of {totalPages}
      </span>
      <button data-testid={`${testid}-next`} disabled={page >= totalPages} onClick={() => onChange(page + 1)} className={navBtn}>
        {nextLabel} <ChevronRight size={15} />
      </button>
    </div>
  );
}
